import React from "react";
import Reveal from "./Reveal";
import Window from "./Window";
import { VscGitCommit, VscTerminal } from "react-icons/vsc";

const roles = [
  {
    hash: "9f3a1c7",
    refs: ["HEAD", "main"],
    company: "JLR",
    title: "Software Graduate",
    type: "Full-time",
    period: "Sep 2024 — Present",
    location: "Coventry, UK · Hybrid",
    summary:
      "Advancing my engineering and interpersonal skills across four graduate rotations spanning embedded, web and full-stack development — working with many stakeholders. Recognised for hard work and achievements at JLR.",
    sections: [
      {
        label: "Full-Stack Web Development",
        points: [
          "Developed internal web applications using React frontends with Python backends.",
          "Built and maintained automated end-to-end test suites using Playwright.",
          "Worked within Agile teams, delivering features through CI/CD pipelines (GitLab).",
          "Collaborated with product owners and stakeholders to gather and refine software requirements.",
        ],
      },
      {
        label: "Embedded Software (ECU Development)",
        points: [
          "Developed embedded C software for ECUs deployed in JLR vehicles.",
          "Worked with automotive development standards and CI/CD-driven build and test workflows.",
          "Gained hands-on experience of the full embedded software lifecycle, from requirements to in-vehicle integration.",
        ],
      },
      {
        label: "Inverter Software (current)",
        points: [
          "Automated Jira management using Python, reducing manual project admin.",
          "Developed code in MATLAB for our high-voltage inverter hardware.",
          "Working with various stakeholders to gather software requirements.",
          "Working collectively as a team to develop our inverters.",
        ],
      },
    ],
  },
  {
    hash: "e4b8d02",
    refs: [],
    company: "Direct Line Group",
    title: "Customer Service Advisor",
    type: "Full-time",
    period: "Jul 2022 — Aug 2024",
    location: "Leeds, UK",
    points: [
      "Dealing with complaints in a pleasant, professional manner.",
      "Remediating customer issues on their insurance policies.",
      "Working under deadlines while balancing vast workloads.",
      "Hitting targets for customer satisfaction and ratings.",
      "Working collectively as a team to guide customers through their insurance.",
    ],
  },
  {
    hash: "a17c5f9",
    refs: [],
    company: "Egress Software Technologies",
    title: "IT Operations Placement",
    type: "Internship",
    period: "Jul 2021 — Nov 2022",
    location: "Sheffield, UK",
    points: [
      "Enhanced internal IT infrastructure, including building web apps to replace legacy tooling.",
      "Worked with Security Operations on vulnerability remediation; handled sensitive data to ISO 27001.",
      "Supported on-prem & cloud infra (Windows Server, CentOS), Azure, Office 365 and Salesforce.",
      "Built a PHP application integrated with Azure Active Directory.",
    ],
  },
  {
    hash: "c30e6ab",
    refs: [],
    company: "McDonald's",
    title: "Crew Trainer",
    type: "Part-time",
    period: "Apr 2018 — Jun 2021",
    location: "Rochdale, UK",
    points: [
      "Promoted to Crew Trainer after five months — the fastest promotion to the role in-store.",
      "Now lead training across the store, ensuring training levels and standards are consistently met.",
      "Took on management tasks, coordinating staff development in a fast-paced setting.",
    ],
  },
];

const Bullets = ({ points }) => (
  <ul className="mt-2 space-y-1.5">
    {points.map((point, pi) => (
      <li key={pi} className="flex gap-2.5 leading-relaxed text-zinc-400">
        <span className="mt-0.5 flex-shrink-0 text-syntax-str">+</span>
        <span>{point}</span>
      </li>
    ))}
  </ul>
);

const Work = () => {
  return (
    <section name="work" className="relative w-full py-24 md:py-28">
      <div className="mx-auto max-w-4xl px-4">
        <Reveal className="mb-8">
          <p className="kicker">
            <span className="text-syntax-comment">03 /</span>
            <span className="text-term-green">experience</span>
          </p>
        </Reveal>

        <Reveal>
          <Window
            file="experience.log"
            icon={<VscTerminal className="text-term-green" />}
            right={<span className="hidden text-xs text-syntax-comment sm:inline">git log --oneline</span>}
          >
            <div className="p-5 text-[0.85rem] sm:p-7 sm:text-[0.9rem]">
              <p className="prompt mb-6 text-syntax-comment">
                <span className="text-zinc-300">git log --author="Khubayb"</span>
              </p>

              <div className="space-y-8">
                {roles.map((role, i) => (
                  <Reveal
                    key={role.hash}
                    delay={i * 70}
                    className="relative border-l border-editor-border pb-1 pl-6 last:border-transparent"
                  >
                    {/* commit node on the graph line */}
                    <VscGitCommit className="absolute -left-[9px] top-0 bg-editor-panel text-syntax-num" size={18} />

                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-syntax-comment">commit</span>
                      <span className="text-syntax-num">{role.hash}</span>
                      {role.refs.length > 0 && (
                        <span className="text-syntax-comment">
                          (
                          {role.refs.map((r, ri) => (
                            <span key={r}>
                              <span className={r === "HEAD" ? "text-syntax-prop" : "text-term-green"}>
                                {r}
                              </span>
                              {ri < role.refs.length - 1 && <span>, </span>}
                            </span>
                          ))}
                          )
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-syntax-comment">
                      Author: <span className="text-zinc-400">Khubayb Kapila</span>
                    </p>
                    <p className="text-syntax-comment">
                      Date:{"   "}<span className="text-zinc-400">{role.period}</span>
                      <span className="text-syntax-comment"> · {role.location}</span>
                    </p>

                    <h3 className="mt-3 font-bold text-white">
                      {role.title}{" "}
                      <span className="font-normal text-syntax-key">@ {role.company}</span>
                      <span className="ml-2 rounded border border-editor-border px-1.5 py-0.5 align-middle text-[0.7rem] font-normal text-syntax-comment">
                        {role.type}
                      </span>
                    </h3>

                    {role.summary && (
                      <p className="mt-2 leading-relaxed text-zinc-400">{role.summary}</p>
                    )}

                    {/* Grouped rotations (JLR) */}
                    {role.sections?.map((section) => (
                      <div key={section.label} className="mt-4">
                        <p className="flex items-center gap-2 text-syntax-prop">
                          <span className="text-syntax-comment">▸</span>
                          {section.label}
                        </p>
                        <Bullets points={section.points} />
                      </div>
                    ))}

                    {/* Flat bullet list */}
                    {role.points?.length > 0 && <Bullets points={role.points} />}
                  </Reveal>
                ))}
              </div>

              <p className="prompt mt-8 text-syntax-comment">
                <span className="animate-blink text-term-green">▊</span>
              </p>
            </div>
          </Window>
        </Reveal>
      </div>
    </section>
  );
};

export default Work;
